/** Earn: Overview (tier and claim, starter journey, this week, more ways to earn, points), Rewards (volume rewards), Missions (weekly missions, streak, milestones), Store, Referrals, Leaderboard, Benefits (tiers and badges) and Yield, in the My account frame. */
import { useEffect, useState } from 'react';
import {
  badgeCatalog,
  fmtCompactUsd,
  fmtUsd,
  MISSION_GROUP_LABEL,
  QUESTS,
  tierFor,
  type MissionsOverview,
  type MissionState,
  type QuestDef,
  type ReferralsOverview,
  type RewardsOverview,
  type SeasonOverview,
  type StoreOverview,
  type VolumeRewardsOverview,
} from '@platform/shared';
import { Glyph, type GlyphName } from '../../components/NotificationIcon';
import { cx, Spinner } from '../../components/ui';
import { Link, navigate, usePath } from '../../lib/router';
import { useLoad } from '../../stores/account';
import { useFeatureState } from '../../stores/features';
import { useAccountKind, useMe } from '../../stores/session';
import { Notice } from '../account/kit';
import { ApplyCodeModal, FriendMilestones, InviteModal, ReferralBox, ReferralLevels, ReferralTable } from './Referral';
import {
  BadgeChip,
  btn,
  ClaimAllButton,
  EarnBadge,
  EarnLayout,
  EarnRow,
  fmtMult,
  fmtPct,
  fmtPts,
  HeroPanel,
  Panel,
  Progress,
  PtsTag,
  RowLink,
  setTitle,
  TierBadge,
  tierById,
  TierEmblem,
  type Msg,
} from './shared';
import { RewardsTab, volumeTeaser } from './RewardsTab';
import { StoreTab } from './StoreTab';
import { YieldTab } from './YieldTab';

const claimable = (d: RewardsOverview) => Number(d.cashback.claimable) + Number(d.referral.claimable);

/** The glyph for a quest: by ladder, or by what the starter step asks for. */
const QUEST_GLYPH: Record<string, GlyphName> = { verify: 'user', two_fa: 'shieldCheck', deposit: 'wallet', first_trade: 'candles', sl_trade: 'shield', refer_1: 'invite' };
const LADDER_GLYPH: Record<string, GlyphName> = { volume: 'chart', friends: 'invite', days: 'candles', streak: 'flame' };
const questGlyph = (q: QuestDef): GlyphName => QUEST_GLYPH[q.id] ?? (q.ladder ? LADDER_GLYPH[q.ladder] : undefined) ?? (q.id.startsWith('academy_') ? 'cap' : 'star');
const MISSION_GLYPH: Record<MissionState['group'], GlyphName> = { trading: 'candles', safety: 'shield', explore: 'star' };

// ---------- Overview ----------

/** Rewards per day, last 8 days: one series, so no legend; hover/focus a day for its value. */
function RewardsChart({ daily }: { daily: RewardsOverview['daily'] }) {
  const [hi, setHi] = useState<number | null>(null);
  const W = 300;
  const H = 150;
  const L = 38;
  const B = 124;
  const T = 14;
  const vals = daily.map((d) => Number(d.usd));
  const rawMax = Math.max(...vals, 0);
  const max = rawMax <= 0 ? 1 : Math.ceil(rawMax / Math.pow(10, Math.floor(Math.log10(rawMax)))) * Math.pow(10, Math.floor(Math.log10(rawMax)));
  const x = (i: number) => L + 6 + (i * (W - L - 12)) / Math.max(1, daily.length - 1);
  const y = (v: number) => B - (v / max) * (B - T);
  const pts = daily.map((d, i) => `${x(i)},${y(Number(d.usd))}`).join(' ');
  const label = (d: string) => new Date(d + 'T00:00:00Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  const h = hi ?? daily.length - 1;
  return (
    <Panel title="USDT rewards" icon="chart" aside={<span className="num text-[12px] text-muted">{label(daily[h]!.day)} · {fmtUsd(daily[h]!.usd)}</span>}>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-[160px] w-full" role="img" aria-label={`Rewards earned per day for the last ${daily.length} days`} onMouseLeave={() => setHi(null)}>
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={L} x2={W - 4} y1={y(max * f)} y2={y(max * f)} stroke={f === 0 ? 'var(--border-strong)' : 'var(--border)'} />
            <text x={L - 6} y={y(max * f) + 3} textAnchor="end" fill="var(--muted)" fontSize="9.5" fontFamily="var(--font-mono)">
              {fmtCompactUsd(String(max * f))}
            </text>
          </g>
        ))}
        <polyline points={pts} fill="none" stroke="var(--accent)" strokeWidth={2} strokeLinejoin="round" />
        {daily.map((d, i) => (
          <g key={d.day}>
            <rect x={x(i) - 16} y={T} width={32} height={B - T} fill="transparent" onMouseEnter={() => setHi(i)} />
            {(i === 0 || i === daily.length - 1 || i === Math.floor(daily.length / 2)) && (
              <text x={x(i)} y={H - 6} textAnchor="middle" fill="var(--muted)" fontSize="9.5" fontFamily="var(--font-mono)">
                {label(d.day)}
              </text>
            )}
          </g>
        ))}
        <circle cx={x(h)} cy={y(Number(daily[h]!.usd))} r={4} fill="var(--accent)" stroke="var(--panel)" strokeWidth={2} />
      </svg>
    </Panel>
  );
}

/** Inside the overview hero: what's ready to claim, split by source, and the one Claim button. */
function ClaimPanel({ data, onDone }: { data: RewardsOverview; onDone: (m: Msg) => void }) {
  const me = useMe();
  const live = me?.accounts.find((a) => a.kind === 'live');
  const rows = [
    { label: 'Cashback', value: data.cashback.claimable, glyph: 'wallet' as const },
    { label: 'Referral rewards', value: data.referral.claimable, glyph: 'invite' as const },
  ];
  return (
    <div className="flex min-w-0 flex-[1_1_300px] flex-col gap-4 rounded-[22px] border border-line bg-card/70 p-5 md:p-6">
      <div className="flex flex-col">
        <span className="text-[13px] text-muted">Ready to claim</span>
        <span className="num text-[34px] font-semibold tracking-tight md:text-[40px]">{fmtUsd(String(claimable(data)))}</span>
      </div>
      <div className="flex flex-col gap-1.5">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center gap-3">
            <EarnBadge glyph={r.glyph} tone={Number(r.value) > 0 ? 'accent' : 'muted'} size="xs" />
            <span className="flex-1 text-[14px]">{r.label}</span>
            <span className="num text-[14px] font-semibold">{fmtUsd(r.value)}</span>
          </div>
        ))}
      </div>
      <ClaimAllButton amount={claimable(data)} onDone={onDone} size="lg" className="w-full" />
      <Link to="/earn/benefits" className={cx(btn('secondary', 'lg'), 'w-full')}>
        See tier benefits
      </Link>
      <p className="m-0 -mt-1 text-center text-[12px] text-muted">Pays to your live account{live ? ` ACC-${live.id.replace(/-/g, '').slice(0, 8).toUpperCase()}` : ''}</p>
    </div>
  );
}

const questPts = (data: RewardsOverview, q: QuestDef) => data.program.settings.questPoints[q.id] ?? q.points;

/** The starter steps in order, the next one highlighted; the only place they're listed. Gone once all are done. Phones list only what's left. */
function StarterCard({ data }: { data: RewardsOverview }) {
  const steps = QUESTS.filter((q) => q.starter).map((q) => ({ q, done: !!data.quests.find((s) => s.id === q.id)?.done }));
  const doneCount = steps.filter((s) => s.done).length;
  if (doneCount === steps.length) return null;
  const current = steps.findIndex((s) => !s.done);
  const left = steps.filter((s) => !s.done);
  const leftPts = left.reduce((t, s) => t + questPts(data, s.q), 0);
  return (
    <Panel
      title="Starter journey"
      icon="star"
      aside={
        <span className="flex items-center gap-2.5 text-[13px] whitespace-nowrap text-muted">
          {/* Phones show the count alone: the bars don't fit beside the title. */}
          <span className="flex gap-1 max-sm:hidden" aria-hidden>
            {steps.map((s, i) => (
              <i key={s.q.id} className={cx('h-1 w-5 rounded-full md:w-6', s.done ? 'bg-up' : i === current ? 'bg-accent' : 'bg-line-strong')} />
            ))}
          </span>
          {doneCount} of {steps.length} done
        </span>
      }
    >
      <p className="m-0 -mt-2 text-[14px] text-muted">
        Finish {left.length} more {left.length === 1 ? 'step' : 'steps'} for {fmtPts(leftPts)} points. This card goes away when you’re done.
      </p>
      <div className="grid gap-1.5 md:grid-cols-2">
        {steps.map(({ q, done }, i) => {
          const isNext = i === current;
          return (
            <EarnRow
              key={q.id}
              badge={<EarnBadge glyph={done ? 'check' : questGlyph(q)} tone={done ? 'done' : isNext ? 'next' : 'base'} />}
              title={q.title}
              aside={<PtsTag n={questPts(data, q)} done={done} />}
              body={q.text}
              className={cx(isNext && 'bg-accent-bg', done && 'max-md:hidden')}
            >
              {done ? (
                <span className="mt-1 text-[13px] font-semibold text-up">Completed</span>
              ) : isNext ? (
                <Link to={q.to} className={cx(btn('primary', 'sm'), 'mt-2 self-start')}>
                  {q.cta}
                </Link>
              ) : (
                <RowLink to={q.to}>{q.cta}</RowLink>
              )}
            </EarnRow>
          );
        })}
      </div>
    </Panel>
  );
}

/** Progress quests (volume, days, streak) as ladders: done steps in green, the next one ringed. Friends have theirs in Referrals. */
const LADDERS = {
  volume: { title: 'Lifetime volume', left: (n: number) => `${fmtCompactUsd(String(n))} to go` },
  days: { title: 'Days traded', left: (n: number) => `${n} ${n === 1 ? 'day' : 'days'} to go` },
  streak: { title: 'Weekly streak', left: (n: number) => `${n} ${n === 1 ? 'week' : 'weeks'} to go` },
} as const;
type Ladder = keyof typeof LADDERS;
const stepLabel = (q: QuestDef) => (q.ladder === 'volume' ? (q.target! >= 1_000_000 ? `${q.target! / 1_000_000}M` : `${q.target! / 1000}K`) : String(q.target));

function MilestonesCard({ data }: { data: RewardsOverview }) {
  const groups = (Object.keys(LADDERS) as Ladder[]).map((l) => ({ id: l, ...LADDERS[l], ids: QUESTS.filter((q) => q.ladder === l).map((q) => q.id) }));
  return (
    <Panel title="Milestones" icon="trophy">
      <div className="grid gap-3 md:grid-cols-3">
        {groups.map((g) => {
          const rows = g.ids.map((id) => ({ q: QUESTS.find((q) => q.id === id)!, st: data.quests.find((s) => s.id === id)! }));
          const next = rows.find((r) => !r.st.done);
          return (
            <div key={g.title} className="flex flex-col gap-1 rounded-[18px] border border-line p-4">
              <div className="mb-1 flex items-center gap-2.5">
                <EarnBadge glyph={LADDER_GLYPH[g.id]!} tone="accent" size="sm" />
                <b className="text-[14.5px]">{g.title}</b>
              </div>
              <ol className="m-0 flex list-none flex-col p-0">
                {rows.map(({ q, st }) => {
                  const go = next?.q.id === q.id;
                  return (
                    <li key={q.id} className="grid grid-cols-[32px_minmax(0,1fr)_auto] items-center gap-3 border-t border-line py-2.5">
                      <span
                        className={cx(
                          'num grid size-8 place-items-center rounded-full text-[10.5px] font-semibold',
                          st.done ? 'bg-up text-bg' : go ? 'bg-accent text-white' : 'bg-track text-muted',
                        )}
                      >
                        {st.done ? <Glyph name="check" size={16} /> : stepLabel(q)}
                      </span>
                      <span className="text-[14px]">
                        {q.title}
                        {go && <small className="block text-[12.5px] text-accent-fg">{g.left((q.target ?? 0) - (st.progress ?? 0))}</small>}
                      </span>
                      <span className="num text-[13px] font-semibold">+{fmtPts(questPts(data, q))}</span>
                    </li>
                  );
                })}
              </ol>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

const progressLabel = (m: MissionState) => (m.unit === 'usd' ? `${fmtCompactUsd(String(m.progress))} of ${fmtCompactUsd(String(m.target))}` : `${m.progress} of ${m.target}`);

function resetsIn(end: string) {
  const ms = Math.max(0, Date.parse(end) - Date.now());
  const d = Math.floor(ms / 86_400_000);
  const h = Math.floor((ms % 86_400_000) / 3_600_000);
  return d > 0 ? `Resets in ${d}d ${h}h` : `Resets in ${h}h ${Math.floor((ms % 3_600_000) / 60_000)}m`;
}

/** Overview: this week's three missions as rows, with a link to the Missions page. */
function ThisWeekCard({ m }: { m: MissionsOverview }) {
  return (
    <Panel title="This week" icon="clock" aside={<span className="rounded-md bg-track px-2.5 py-1 text-[12.5px] font-medium text-muted">{resetsIn(m.week.end)}</span>}>
      <div className="-mx-3 flex flex-col gap-0.5">
        {m.missions.map((x) => (
          <EarnRow key={x.id} badge={<EarnBadge glyph={x.done ? 'check' : MISSION_GLYPH[x.group]} tone={x.done ? 'done' : 'base'} />} title={x.title} aside={<PtsTag n={x.points} done={x.done} />}>
            <Progress value={x.progress} max={x.target} label={x.title} color={x.done ? 'var(--green)' : undefined} className="mt-1.5" />
            <span className="num mt-0.5 text-[12.5px] text-muted">{x.done ? 'Completed' : progressLabel(x)}</span>
          </EarnRow>
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
        <span className="inline-flex items-center gap-2 text-[13.5px] text-muted">
          <span className="text-warn">
            <Glyph name="flame" size={16} />
          </span>
          Streak <b className="text-fg">{m.streak.current} {m.streak.current === 1 ? 'week' : 'weeks'}</b>
        </span>
        <RowLink to="/earn/missions">All missions</RowLink>
      </div>
    </Panel>
  );
}

/** Referrals, volume rewards, the Academy and the store as one list, each with the step that pays next. */
function WaysToEarn({ data, volume }: { data: RewardsOverview; volume: VolumeRewardsOverview | null }) {
  const cfg = data.program.settings;
  const academy = QUESTS.filter((q) => q.id.startsWith('academy_')).map((q) => ({ q, done: !!data.quests.find((s) => s.id === q.id)?.done }));
  const nextLesson = academy.find((a) => !a.done);
  const vt = volume ? volumeTeaser(volume) : null;
  return (
    <Panel title="More ways to earn" icon="gift">
      <div className="-mx-3 flex flex-col gap-0.5">
        <EarnRow
          badge={<EarnBadge glyph="invite" tone="accent" />}
          title="Invite friends"
          aside={`${data.referrals} ${data.referrals === 1 ? 'friend' : 'friends'}`}
          body={`Get ${fmtUsd(String(cfg.referral.bonusUsd))} for every friend who verifies and trades, plus ${fmtPct(cfg.referral.levelPcts[0])} of their fees.`}
        >
          <RowLink to="/earn/referrals">Invite friends</RowLink>
        </EarnRow>
        {vt && (
          <EarnRow badge={<EarnBadge glyph="zap" tone="accent" />} title={vt.title} aside={volume!.summary.readyCount > 0 ? 'Ready' : undefined} body={vt.text}>
            <RowLink to="/earn/rewards">View rewards</RowLink>
          </EarnRow>
        )}
        {academy.length > 0 && (
          <EarnRow
            badge={<EarnBadge glyph="cap" tone={nextLesson ? 'base' : 'done'} />}
            title="Academy"
            aside={`${academy.filter((a) => a.done).length} of ${academy.length} done`}
            body={nextLesson ? `${nextLesson.q.title} for ${fmtPts(questPts(data, nextLesson.q))} points.` : 'Every Academy quest done.'}
          >
            <RowLink to={nextLesson?.q.to ?? '/academy'}>{nextLesson ? 'Continue learning' : 'Open Academy'}</RowLink>
          </EarnRow>
        )}
        <EarnRow badge={<EarnBadge glyph="bag" />} title="Store" aside={`${fmtPts(data.points.balance)} pts`} body="Spend points on fee discounts, cashback boosts and streak freezes.">
          <RowLink to="/earn/store">Open store</RowLink>
        </EarnRow>
      </div>
    </Panel>
  );
}

function OverviewTab({ data, missions, volume, onMsg }: { data: RewardsOverview; missions: MissionsOverview | null; volume: VolumeRewardsOverview | null; onMsg: (m: Msg) => void }) {
  const cfg = data.program.settings;
  const vol = Number(data.volume);
  const { tier, next } = tierFor(vol, cfg.tiers);
  const earned = Number(data.cashback.earned) + Number(data.referral.earned);
  return (
    <>
      <HeroPanel>
        <div className="flex flex-wrap gap-7">
          <div className="flex min-w-0 flex-[999_1_480px] flex-col gap-5">
            <span className="self-start rounded-lg bg-accent-bg px-2.5 py-1 text-[12.5px] font-semibold text-accent-fg">{fmtPct(tier.cashbackPct)} cashback on your fees</span>
            <div className="flex items-center gap-4">
              <TierEmblem tier={tier} size={60} />
              <h2 className="m-0 text-[24px] font-semibold tracking-tight md:text-[32px]">
                {tier.name} · {fmtMult(tier.multiplier)} points
              </h2>
            </div>
            <div className="flex flex-col gap-2">
              <Progress value={next ? vol - tier.minVolume : 1} max={next ? next.minVolume - tier.minVolume : 1} label="Progress to the next tier" />
              <span className="text-[14px] text-muted">
                {next ? (
                  <>
                    Trade <b className="num text-fg">{fmtCompactUsd(String(next.minVolume - vol))}</b> more to reach <b className="text-fg">{next.name}</b>: {fmtMult(next.multiplier)} points and {fmtPct(next.cashbackPct)} cashback
                  </>
                ) : (
                  "You're at the top tier."
                )}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
              {(
                [
                  [fmtPts(data.points.total), 'Points'],
                  [fmtPts(data.points.balance), 'To spend'],
                  [fmtUsd(String(earned)), 'Earned all time'],
                  [data.rank ? `#${fmtPts(data.rank)}` : '—', 'Rank'],
                ] as const
              ).map(([v, l]) => (
                <div key={l} className="flex min-w-0 flex-col rounded-2xl border border-line bg-card/60 px-4 py-3.5">
                  <span className="num truncate text-[22px] font-semibold">{v}</span>
                  <span className="text-[13px] text-muted">{l}</span>
                </div>
              ))}
            </div>
          </div>
          <ClaimPanel data={data} onDone={onMsg} />
        </div>
      </HeroPanel>

      <StarterCard data={data} />

      <div className="grid gap-4 md:gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        {missions ? <ThisWeekCard m={missions} /> : <RewardsChart daily={data.daily} />}
        <WaysToEarn data={data} volume={volume} />
      </div>

      <div className={cx('grid gap-4 md:gap-6', missions && 'lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]')}>
        <Panel
          title="Where your points come from"
          icon="pie"
          className="scroll-mt-6"
          aside={
            <span className="num text-[13px] text-muted">
              {data.points.spent > 0 ? `${fmtPts(data.points.spent)} spent · ${fmtPts(data.points.balance)} to spend` : `${fmtPts(data.points.total)} total`}
            </span>
          }
        >
          <div id="points-breakdown" className="-mx-3 grid gap-x-4 gap-y-0.5 sm:grid-cols-2">
            {(
              [
                ['chart', 'Trading', data.points.trading, `${fmtPts(cfg.pointsPerUsd)} USDT of volume = 1 point, × ${fmtMult(tier.multiplier)} for ${tier.name}`],
                ['check', 'Quests', data.points.quests, `${data.quests.filter((q) => q.done).length} of ${data.quests.filter((q) => q.done || !QUESTS.find((d) => d.id === q.id)?.retired).length} quests done`],
                ['target', 'Missions', data.points.missions, 'Weekly missions and streak bonuses'],
                ['trophy', 'Tiers reached', data.points.tiers, 'One-time points for each tier you reach'],
                ['star', 'Season prizes', data.points.seasons, 'Prizes from the seasons you placed in'],
                ['invite', 'Friends', data.points.referral, `${fmtPct(cfg.referral.pointsPct)} of your level 1 friends' points`],
              ] as const
            ).map(([g, l, v, sub]) => (
              <EarnRow key={l} badge={<EarnBadge glyph={g} />} title={l} aside={<span className="text-[15px] font-semibold text-fg">{fmtPts(v)}</span>} body={sub} />
            ))}
          </div>
        </Panel>
        {missions && <RewardsChart daily={data.daily} />}
      </div>
    </>
  );
}

// ---------- Missions tab ----------

function MissionCard({ m }: { m: MissionState }) {
  return (
    <div className={cx('flex min-w-0 flex-col gap-2.5 rounded-[20px] border p-5', m.done ? 'border-up/40 bg-up-bg' : 'border-line bg-card/70')}>
      <div className="flex items-center justify-between gap-2">
        <EarnBadge glyph={m.done ? 'check' : MISSION_GLYPH[m.group]} tone={m.done ? 'done' : 'next'} size="lg" />
        <span className="rounded-md bg-track px-2 py-0.5 text-[12px] font-medium text-muted">{MISSION_GROUP_LABEL[m.group]}</span>
      </div>
      <b className="mt-1 text-[17px] font-semibold">{m.title}</b>
      <span className="text-[14px] text-muted">{m.text}</span>
      <Progress value={m.progress} max={m.target} label={m.title} color={m.done ? 'var(--green)' : undefined} />
      <div className="mt-auto flex items-center justify-between gap-3 pt-1 text-[13px] text-muted">
        <span className="num">
          {m.done ? 'Completed' : progressLabel(m)} · <b className={m.done ? 'text-up' : 'text-accent-fg'}>+{fmtPts(m.points)}</b>
        </span>
        {m.done ? (
          <span className="flex items-center gap-1 font-semibold text-up">
            <Glyph name="check" size={15} /> Done
          </span>
        ) : (
          <Link to={m.to} className={btn('secondary', 'sm')}>
            {m.cta}
          </Link>
        )}
      </div>
    </div>
  );
}

function MissionsTab({ data, m }: { data: RewardsOverview; m: MissionsOverview }) {
  const start = new Date(m.week.start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  const s = m.streak;
  return (
    <>
      <HeroPanel glow="50% 130%">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="m-0 text-[24px] font-semibold md:text-[30px]">Weekly missions</h2>
            <p className="m-0 mt-1 text-[14px] text-muted">Week of {start} · new missions every Monday at 00:00 UTC</p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-accent-bg px-3 py-1.5 text-[13px] font-semibold text-accent-fg">
            <Glyph name="clock" size={14} />
            {resetsIn(m.week.end)}
          </span>
        </div>
        {m.missions.length === 0 ? (
          <p className="m-0 py-8 text-center text-[15px] text-muted">No missions this week.</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-3">
            {m.missions.map((x) => (
              <MissionCard key={x.id} m={x} />
            ))}
          </div>
        )}
        <div className="flex flex-col gap-3 rounded-[20px] border border-line bg-card/70 p-4 md:p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <b className="inline-flex items-center gap-2.5 text-[15px]">
              <EarnBadge glyph="flame" tone="warn" size="sm" />
              Weekly streak · {s.current} {s.current === 1 ? 'week' : 'weeks'}
            </b>
            <span className="text-[13px] text-muted">
              Finish {s.needed} of 3 missions a week to keep it. Best: {s.best} {s.best === 1 ? 'week' : 'weeks'}.
            </span>
          </div>
          <div className="grid grid-cols-9 gap-1.5" aria-label={`Streak: ${s.current} weeks`}>
            {s.weeks.map((w, i) => {
              const now = i === s.weeks.length - 1;
              return (
                <span
                  key={w.week}
                  title={w.frozen ? `Week of ${w.week}: kept by a streak freeze` : `Week of ${w.week}: ${w.done} done`}
                  className={cx(
                    'num grid h-9 place-items-center rounded-[10px] text-[11px]',
                    w.qualified ? 'bg-up-bg font-semibold text-up' : 'bg-track text-muted',
                    w.frozen && 'bg-accent-bg text-accent-fg',
                    now && !w.qualified && 'outline-2 -outline-offset-2 outline-accent outline-dashed text-accent-fg',
                  )}
                >
                  {now ? 'Now' : w.frozen ? <Glyph name="shield" size={14} /> : new Date(w.week + 'T00:00:00Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'numeric', timeZone: 'UTC' })}
                </span>
              );
            })}
          </div>
          <span className="text-[13px] text-muted">
            {s.weeks[s.weeks.length - 1]!.qualified ? "You've kept your streak this week: a bonus of " : 'Keep it this week for a bonus of '}
            <b className="text-fg">{fmtPct(s.bonusPct)}</b> on this week's mission points, paid when the week ends. {fmtPts(m.weekPoints)} of {fmtPts(m.weeklyCap)} mission points earned this week; only your live account counts.
          </span>
        </div>
      </HeroPanel>

      <MilestonesCard data={data} />

      <Panel title="History" icon="clock">
        {m.history.length === 0 ? (
          <p className="m-0 py-6 text-center text-[15px] text-muted">Missions you finish show up here.</p>
        ) : (
          <div className="-mx-3 flex flex-col gap-0.5">
            {m.history.map((h) => (
              <EarnRow
                key={`${h.week}-${h.id}`}
                className="items-center"
                badge={<EarnBadge glyph={h.id === 'streak_bonus' ? 'flame' : 'check'} tone="done" size="sm" />}
                title={
                  <>
                    {h.title}
                    {h.id === 'streak_bonus' && <span className="font-normal text-muted"> · week of {new Date(h.week + 'T00:00:00Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })}</span>}
                  </>
                }
                aside={<span className="text-[14px] font-semibold text-up">+{fmtPts(h.points)}</span>}
                body={`Finished ${new Date(h.completedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
              />
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

function ReferralsTab({ data, refs, onMsg }: { data: RewardsOverview; refs: ReferralsOverview; onMsg: (m: Msg) => void }) {
  const cfg = refs.program.settings;
  const [apply, setApply] = useState(false);
  const [invite, setInvite] = useState(false);
  return (
    <>
      <ReferralBox data={refs} onInvite={() => setInvite(true)} />
      <div className="grid gap-4 md:gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <FriendMilestones data={refs} quests={data.quests} />
        <ReferralTable data={refs} onApply={refs.canApply ? () => setApply(true) : undefined} />
      </div>
      <ReferralLevels data={refs} />
      {refs.referredBy && (
        <p className="m-0 text-center text-[14px] text-muted">
          You joined with code <b className="num text-fg">{refs.referredBy}</b>.
        </p>
      )}
      <ApplyCodeModal open={apply} onClose={() => setApply(false)} days={cfg.referral.applyWindowDays} onDone={onMsg} />
      <InviteModal open={invite} onClose={() => setInvite(false)} code={refs.code} bonus={cfg.referral.bonusUsd} />
    </>
  );
}

// ---------- Leaderboard tab ----------

/** The name clients appear under on both boards: generated at sign-up (NeonFalcon42), can't be changed. */
function UsernameRow() {
  const me = useMe();
  if (!me) return null;
  return (
    <div className="text-[14px] text-muted">
      You appear as <b className="text-fg">{me.username}</b>
    </div>
  );
}

function Badges({ ids, tiers }: { ids: string[]; tiers: RewardsOverview['program']['settings']['tiers'] }) {
  if (!ids.length) return null;
  return (
    <span className="flex shrink-0 gap-1 max-sm:hidden">
      {ids.map((id) => (
        <BadgeChip key={id} id={id} tiers={tiers} />
      ))}
    </span>
  );
}

const shortDate = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
function endsIn(iso: string) {
  const ms = Math.max(0, Date.parse(iso) - Date.now());
  const d = Math.floor(ms / 86_400_000);
  return d >= 1 ? `ends in ${d} ${d === 1 ? 'day' : 'days'}` : `ends in ${Math.max(1, Math.floor(ms / 3_600_000))}h`;
}

function SeasonBoard({ data, s }: { data: RewardsOverview; s: SeasonOverview }) {
  const tiers = data.program.settings.tiers;
  const podium = s.leaderboard.length >= 3 ? [s.leaderboard[1]!, s.leaderboard[0]!, s.leaderboard[2]!] : [];
  const rest = podium.length ? s.leaderboard.slice(3) : s.leaderboard;
  const youListed = s.leaderboard.some((r) => r.you);
  const medal = ['', 'bg-[#f5c542]', 'bg-[#b8c0cc]', 'bg-[#cd7f32]'];
  return (
    <>
      <HeroPanel glow="50% 130%">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="m-0 text-[24px] font-semibold md:text-[30px]">{s.season.name}</h2>
            <p className="m-0 mt-1 text-[14px] text-muted">
              {shortDate(s.season.startsAt)} – {shortDate(new Date(Date.parse(s.season.endsAt) - 1).toISOString())} · {endsIn(s.season.endsAt)} · {fmtPts(s.rankedUsers)} traders
            </p>
            <p className="m-0 mt-1 text-[13px] text-muted">Points you earn this season from trading, missions and friends. Updates every minute.</p>
          </div>
          <div className="flex gap-2.5">
            <div className="flex flex-col rounded-2xl border border-accent/50 bg-card/70 px-4 py-3">
              <span className="num text-[24px] font-semibold">{s.rank ? `#${fmtPts(s.rank)}` : '—'}</span>
              <span className="text-[13px] text-muted">Your rank</span>
            </div>
            <div className="flex flex-col rounded-2xl border border-line bg-card/70 px-4 py-3">
              <span className="num text-[24px] font-semibold">{fmtPts(s.points)}</span>
              <span className="text-[13px] text-muted">Season points</span>
            </div>
          </div>
        </div>
        {s.leaderboard.length === 0 ? (
          <div className="py-14 text-center text-muted">No one has season points yet. Trade or finish a mission to be first.</div>
        ) : (
          <>
            {podium.length > 0 && (
              <div className="grid grid-cols-3 items-end gap-3">
                {podium.map((r) => (
                  <div key={r.rank} className={cx('flex min-w-0 flex-col items-center gap-1.5 rounded-[20px] border bg-card/70 px-3 text-center', r.rank === 1 ? 'border-[#f5c542]/50 py-6' : 'border-line py-4', r.you && 'bg-accent-bg')}>
                    <span className={cx('grid place-items-center rounded-full text-black', r.rank === 1 ? 'size-12' : 'size-10', medal[r.rank])}>
                      <Glyph name="trophy" size={r.rank === 1 ? 22 : 18} />
                    </span>
                    <span className="num text-[12px] font-semibold text-muted">#{r.rank}</span>
                    <TierBadge tier={tierById(tiers, r.tierId)} size="sm" />
                    <span className="max-w-full truncate text-[15px] font-semibold">{r.username}</span>
                    <span className="num text-[13px] text-muted">{fmtPts(r.points)}</span>
                  </div>
                ))}
              </div>
            )}
            <ol className="m-0 list-none p-0">
              {rest.map((r) => (
                <li key={r.rank} className={cx('flex items-center gap-3 border-t border-line px-2 py-3.5 text-[15px]', r.you && 'rounded-xl border-transparent bg-accent-bg font-semibold')}>
                  <span className="num w-9 text-center text-muted">{r.rank}</span>
                  <TierBadge tier={tierById(tiers, r.tierId)} size="sm" />
                  <span className="min-w-0 flex-1 truncate">
                    {r.username}
                    {r.you && <span className="font-normal text-muted"> (you)</span>}
                  </span>
                  <Badges ids={r.badges} tiers={tiers} />
                  <span className="num">{fmtPts(r.points)}</span>
                </li>
              ))}
            </ol>
          </>
        )}
        {!youListed && s.rank && (
          <div className="flex items-center gap-3 rounded-xl bg-accent-bg px-2 py-3.5 text-[15px] font-semibold">
            <span className="num w-9 text-center">{fmtPts(s.rank)}</span>
            <span className="flex-1">You</span>
            <span className="num">{fmtPts(s.points)}</span>
          </div>
        )}
      </HeroPanel>

      <div className="grid gap-4 md:gap-6 lg:grid-cols-2">
        <Panel title={`${s.season.name} prizes`} icon="gift">
          {s.season.prizes.length === 0 ? (
            <p className="m-0 text-[15px] text-muted">No prizes this season.</p>
          ) : (
            <div className="-mx-3 flex flex-col gap-0.5">
              {s.season.prizes.map((p) => (
                <div key={p.fromRank} className="flex items-center gap-3 rounded-xl p-3">
                  {p.badge ? <BadgeChip id={p.badge} tiers={tiers} size="md" /> : <EarnBadge glyph="target" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-[14.5px] font-semibold">{p.fromRank === p.toRank ? `#${p.fromRank}` : `#${p.fromRank} to #${p.toRank}`}</div>
                    <div className="text-[13px] text-muted">{p.badge ? `${badgeCatalog(tiers).find((x) => x.id === p.badge)?.name} badge` : 'Points only'}</div>
                  </div>
                  <span className="num text-[16px] font-semibold">+{fmtPts(p.points)}</span>
                </div>
              ))}
            </div>
          )}
          <p className="m-0 text-[13px] text-muted">Prizes are added to your points when the season ends. The next season starts straight away.</p>
        </Panel>
        <Panel title="Your past seasons" icon="clock">
          {s.past.length === 0 ? (
            <p className="m-0 py-6 text-center text-[15px] text-muted">Your results show here when a season ends.</p>
          ) : (
            <div className="-mx-3 flex flex-col gap-0.5">
              {s.past.map((p) => (
                <div key={p.id} className="flex items-center gap-3 rounded-xl p-3">
                  {p.badge ? <BadgeChip id={p.badge} tiers={tiers} size="md" /> : <EarnBadge glyph="star" tone="muted" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-[14.5px] font-semibold">{p.name}</div>
                    <div className="text-[13px] text-muted">{p.rank ? `#${fmtPts(p.rank)} · ${fmtPts(p.points)} points` : 'Not ranked'}</div>
                  </div>
                  {p.prizePoints > 0 && <span className="num text-[16px] font-semibold text-up">+{fmtPts(p.prizePoints)}</span>}
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </>
  );
}

function AllTimeBoard({ data }: { data: RewardsOverview }) {
  const tiers = data.program.settings.tiers;
  const youListed = data.leaderboard.some((r) => r.you);
  return (
    <HeroPanel glow="50% 130%">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="m-0 text-[24px] font-semibold md:text-[30px]">Top traders by points</h2>
          <p className="m-0 mt-1 text-[14px] text-muted">{fmtPts(data.rankedUsers)} traders ranked, all time. Updates every minute.</p>
          {data.samples === 'admin' && (
            <p className="m-0 mt-1 text-[12.5px] text-warn">Admin preview: includes sample traders that change every day. Clients see them only on a demo account.</p>
          )}
        </div>
      </div>
      {data.leaderboard.length === 0 ? (
        <div className="py-14 text-center text-muted">No one has points yet. Be the first.</div>
      ) : (
        <ol className="m-0 list-none p-0">
          {data.leaderboard.map((r) => (
            <li key={r.rank} className={cx('flex items-center gap-3 border-t border-line px-2 py-3.5 text-[15px]', r.you && 'rounded-xl border-transparent bg-accent-bg font-semibold')}>
              <span className={cx('num w-9 text-center text-[15px]', r.rank <= 3 ? 'text-warn' : 'text-muted')}>{r.rank}</span>
              <TierBadge tier={tierById(tiers, r.tierId)} size="sm" />
              <span className="min-w-0 flex-1 truncate">
                {r.username}
                {r.you && <span className="font-normal text-muted"> (you)</span>}
              </span>
              <Badges ids={r.badges} tiers={tiers} />
              <span className="num">{fmtPts(r.points)}</span>
            </li>
          ))}
        </ol>
      )}
      {!youListed && data.rank && (
        <div className="flex items-center gap-3 rounded-xl bg-accent-bg px-2 py-3.5 text-[15px] font-semibold">
          <span className="num w-9 text-center">{fmtPts(data.rank)}</span>
          <span className="flex-1">You</span>
          <span className="num">{fmtPts(data.points.total)}</span>
        </div>
      )}
    </HeroPanel>
  );
}

function LeaderboardTab({ data, season }: { data: RewardsOverview; season: SeasonOverview | null }) {
  const [board, setBoard] = useState<'season' | 'all'>('season');
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="radiogroup" aria-label="Leaderboard" className="flex gap-1 rounded-xl bg-track p-1">
          {(
            [
              ['season', season?.season.name ?? 'Season'],
              ['all', 'All time'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={board === id}
              onClick={() => setBoard(id)}
              className={cx('rounded-lg px-5 py-2 text-[14px] font-semibold', board === id ? 'bg-card text-fg' : 'text-muted hover:text-fg')}
            >
              {label}
            </button>
          ))}
        </div>
        <UsernameRow />
      </div>
      {board === 'all' ? (
        <AllTimeBoard data={data} />
      ) : season ? (
        <SeasonBoard data={data} s={season} />
      ) : (
        <div className="grid min-h-[200px] place-items-center text-muted">
          <Spinner />
        </div>
      )}
    </>
  );
}

// ---------- Benefits tab ----------

/** Every badge: the ones you hold first, then the rest, locked. */
function BadgesPanel({ data }: { data: RewardsOverview }) {
  const tiers = data.program.settings.tiers;
  const held = new Map(data.badges.map((b) => [b.id, b]));
  const all = badgeCatalog(tiers);
  const rows = [...data.badges.map((b) => all.find((d) => d.id === b.id)!).filter(Boolean), ...all.filter((d) => !held.has(d.id))];
  return (
    <Panel title="Badges" icon="star" aside={<span className="num text-[13px] text-muted">{data.badges.length} of {all.length}</span>}>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {rows.map((d) => {
          const b = held.get(d.id);
          return (
            <div key={d.id} className={cx('flex items-center gap-3 rounded-xl p-3', !b && 'opacity-60')}>
              <BadgeChip id={d.id} tiers={tiers} size="md" count={b?.count} />
              <div className="min-w-0 flex-1">
                <div className="text-[15px] font-semibold">
                  {d.name}
                  {b && b.count > 1 && <span className="num font-normal text-muted"> ×{b.count}</span>}
                </div>
                <div className="text-[13px] text-muted">{d.text}</div>
              </div>
              {b ? (
                <span className="rounded-md bg-up-bg px-2 py-0.5 text-[12px] font-semibold text-up">Earned</span>
              ) : (
                <span className="shrink-0 text-muted" title="Locked">
                  <Glyph name="lock" size={15} />
                </span>
              )}
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

function BenefitsTab({ data }: { data: RewardsOverview }) {
  const cfg = data.program.settings;
  const vol = Number(data.volume);
  const { next } = tierFor(vol, cfg.tiers);
  return (
    <>
      <HeroPanel>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="max-w-[680px]">
            <h2 className="m-0 text-[24px] font-semibold md:text-[30px]">Tiers</h2>
            <p className="m-0 mt-1.5 text-[15px] text-muted">Your tier rises with your all-time live trading volume. Higher tiers multiply the points you earn and return more of your trading fees as cashback.</p>
          </div>
          <span className="text-[13.5px] text-muted">
            Lifetime volume <b className="num text-fg">{fmtCompactUsd(data.volume)}</b>
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
          {cfg.tiers.map((t) => {
            const on = t.id === data.tierId;
            const reached = vol >= t.minVolume;
            const isNext = next?.id === t.id;
            return (
              <section
                key={t.id}
                aria-current={on ? 'true' : undefined}
                className={cx('flex min-w-0 flex-col items-center gap-2.5 rounded-[20px] border p-4 text-center', on ? 'border-accent bg-accent-bg' : 'border-line bg-card/70')}
              >
                <TierEmblem tier={t} size={46} dim={!reached && !isNext} />
                <div>
                  <div className="text-[16px] font-semibold">{t.name}</div>
                  <div className="num text-[12px] text-muted">{t.minVolume ? `${fmtCompactUsd(String(t.minVolume))}+ volume` : 'Start here'}</div>
                </div>
                <dl className="m-0 grid w-full grid-cols-2 gap-1.5 text-left">
                  <div className="rounded-lg bg-track px-2.5 py-1.5">
                    <dt className="text-[10.5px] text-muted">Points</dt>
                    <dd className="num m-0 text-[14px] font-semibold">{fmtMult(t.multiplier)}</dd>
                  </div>
                  <div className="rounded-lg bg-track px-2.5 py-1.5">
                    <dt className="text-[10.5px] text-muted">Cashback</dt>
                    <dd className="num m-0 text-[14px] font-semibold">{fmtPct(t.cashbackPct)}</dd>
                  </div>
                </dl>
                <span
                  className={cx(
                    'num rounded-md px-2 py-0.5 text-[11.5px] font-semibold',
                    on ? 'bg-accent text-white' : isNext ? 'bg-accent-bg text-accent-fg' : reached ? 'bg-up-bg text-up' : 'bg-track text-muted',
                  )}
                >
                  {on ? 'Your tier' : isNext ? `${fmtCompactUsd(String(t.minVolume - vol))} to go` : reached ? 'Reached' : t.reachPoints > 0 ? `+${fmtPts(t.reachPoints)} pts` : 'Locked'}
                </span>
              </section>
            );
          })}
        </div>
      </HeroPanel>
      <BadgesPanel data={data} />
    </>
  );
}

// ---------- page ----------

const TAB_OF: Record<string, 'overview' | 'rewards' | 'missions' | 'store' | 'referrals' | 'leaderboard' | 'benefits' | 'yield'> = {
  '/earn': 'overview',
  '/earn/rewards': 'rewards',
  '/earn/missions': 'missions',
  '/earn/store': 'store',
  '/earn/referrals': 'referrals',
  '/earn/leaderboard': 'leaderboard',
  '/earn/benefits': 'benefits',
  '/earn/yield': 'yield',
};

export function EarnPage() {
  const path = usePath().replace(/\/$/, '');
  const tab = TAB_OF[path] ?? 'overview';
  // The account being viewed only decides whether the leaderboard includes sample traders (demo accounts).
  const kind = useAccountKind();
  const rewards = useLoad<RewardsOverview>(`/api/rewards?view=${kind}`);
  const refs = useLoad<ReferralsOverview>(tab === 'referrals' ? '/api/referrals' : null);
  const missions = useLoad<MissionsOverview>(tab === 'overview' || tab === 'missions' ? '/api/missions' : null);
  const season = useLoad<SeasonOverview>(tab === 'leaderboard' ? '/api/seasons/current' : null);
  const store = useLoad<StoreOverview>(tab === 'store' ? '/api/store' : null);
  // Volume rewards: the Rewards tab, and a card on the overview, only while the feature is on.
  const features = useFeatureState();
  const vrOn = features.access.volume_rewards;
  const volume = useLoad<VolumeRewardsOverview>(vrOn && (tab === 'rewards' || tab === 'overview') ? '/api/volume-rewards' : null);
  const vrOff = tab === 'rewards' && features.ready && !vrOn;
  useEffect(() => {
    if (vrOff) navigate('/earn', { replace: true });
  }, [vrOff]);
  const [msg, setMsg] = useState<Msg>(null);
  useEffect(
    () => setTitle({ overview: 'Earn', rewards: 'Volume rewards', missions: 'Missions', store: 'Store', referrals: 'Referrals', leaderboard: 'Leaderboard', benefits: 'Benefits', yield: 'Yield' }[tab]),
    [tab],
  );
  const onMsg = (m: Msg) => {
    setMsg(m);
    void rewards.reload();
    void refs.reload();
    void store.reload();
    void volume.reload();
  };
  const d = rewards.data;
  const tier = d ? tierById(d.program.settings.tiers, d.tierId) : undefined;
  const failed = (tab === 'rewards' ? volume.error : null) ?? rewards.error ?? refs.error ?? missions.error ?? store.error;
  const loading = <div className="grid min-h-[320px] place-items-center text-muted">{failed ? <p>{failed}</p> : <Spinner />}</div>;

  return (
    <EarnLayout tier={tier} action={d && tab !== 'yield' && tab !== 'overview' && tab !== 'rewards' ? <ClaimAllButton amount={claimable(d)} onDone={onMsg} /> : undefined}>
      <Notice msg={msg} onClose={() => setMsg(null)} />
      {tab === 'yield' ? (
        <YieldTab />
      ) : tab === 'rewards' ? (
        volume.data ? <RewardsTab data={volume.data} onMsg={onMsg} /> : loading
      ) : !d ? (
        loading
      ) : tab === 'leaderboard' ? (
        <LeaderboardTab data={d} season={season.data} />
      ) : tab === 'benefits' ? (
        <BenefitsTab data={d} />
      ) : tab === 'referrals' ? (
        refs.data ? <ReferralsTab data={d} refs={refs.data} onMsg={onMsg} /> : loading
      ) : tab === 'store' ? (
        store.data ? <StoreTab data={store.data} onMsg={onMsg} /> : loading
      ) : tab === 'missions' ? (
        missions.data ? <MissionsTab data={d} m={missions.data} /> : loading
      ) : (
        <OverviewTab data={d} missions={missions.data} volume={volume.data} onMsg={onMsg} />
      )}
    </EarnLayout>
  );
}
